import React, { Component, useEffect, useMemo, useState } from 'react';
import { NavLink, useOutletContext, useParams } from 'react-router-dom';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpIcon from '@mui/icons-material/Help';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import WidgetsIcon from '@mui/icons-material/Widgets';

import * as Widgets from '../../../Widgets/Index';
import WidgetsSettings from '../../../Widgets/WidgetsSettings';
import { db } from '../../../store/api/fbConfig';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogTitle-root': {
    paddingRight: theme.spacing(7),
  },
}));

const ADMIN_ROLES = ['admin', 'editor', 'owner'];
const DEFAULT_DATA_LIMIT = 1000;

function canEdit(userRole) {
  return ADMIN_ROLES.includes(userRole);
}

function parseBody(body) {
  if (typeof body !== 'string') return body;

  try {
    return JSON.parse(body);
  } catch (error) {
    console.warn('Invalid JSON string in device data body:', body);
    return body;
  }
}

function getWidgetTitle(widgetKey) {
  return Widgets[widgetKey]?.Settings?.Name || widgetKey;
}

function getVisibleWidgetKeys() {
  return Object.keys(Widgets)
    .filter((key) => {
      const settings = Widgets[key]?.Settings;
      return settings?.Name && settings?.Display !== false;
    })
    .sort((a, b) => getWidgetTitle(a).localeCompare(getWidgetTitle(b)));
}

function makeDefaultWidgetSettings(settings = []) {
  return settings.reduce((acc, setting) => {
    if (!setting?.id) return acc;

    if (setting.type === 'switch' || setting.type === 'checkbox') {
      acc[setting.id] = setting.default === true;
      return acc;
    }

    if (setting.type === 'array') {
      acc[setting.id] = Array.isArray(setting.default) ? setting.default : [];
      return acc;
    }

    acc[setting.id] = setting.default ?? '';
    return acc;
  }, {});
}

function makeNewWidgetDetails(widgetKey, widgetOrder) {
  const widgetSettings = Widgets[widgetKey]?.Settings?.options?.settings || [];

  return {
    widget: widgetKey,
    widgetOrder,
    name: getWidgetTitle(widgetKey),
    xs: 12,
    sm: 12,
    md: 12,
    lg: 12,
    ...makeDefaultWidgetSettings(widgetSettings),
  };
}

class WidgetErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Widget render error:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <Alert severity="error">
          This widget could not be displayed: {this.state.error.message || String(this.state.error)}
        </Alert>
      );
    }

    return this.props.children;
  }
}

function AddWidgetDialog({ open, onClose, onAdd }) {
  const widgetKeys = useMemo(() => getVisibleWidgetKeys(), []);

  return (
    <BootstrapDialog onClose={onClose} open={open} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <WidgetsIcon fontSize="small" />
          <Typography variant="h6" component="span">Add widget</Typography>
        </Stack>

        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {widgetKeys.length === 0 ? (
          <Alert severity="info">No widgets are available.</Alert>
        ) : (
          <Grid container spacing={2}>
            {widgetKeys.map((widgetKey) => {
              const widget = Widgets[widgetKey];
              const title = getWidgetTitle(widgetKey);
              const hasSettings = !!widget?.Settings?.options?.settings?.length;

              return (
                <Grid item xs={12} sm={6} md={4} key={widgetKey}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea onClick={() => onAdd(widgetKey)} sx={{ height: '100%' }}>
                      <CardContent>
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <WidgetsIcon color="primary" />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {title}
                            </Typography>
                          </Stack>

                          <Typography variant="body2" color="text.secondary">
                            {hasSettings ? 'Configurable widget' : 'Simple widget'}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>
    </BootstrapDialog>
  );
}

function EditWidgetDialog({
  open,
  selectedDetails,
  deviceData,
  saving,
  onClose,
  onSave,
  onChange,
}) {
  const widgetKey = selectedDetails?.widget;
  const widgetSettings = widgetKey ? Widgets[widgetKey]?.Settings : null;
  const documentLink = widgetSettings?.document?.url ? `/documents${widgetSettings.document.url}` : null;
  const documentLabel = widgetSettings?.document?.label || `${getWidgetTitle(widgetKey)} help`;

  return (
    <BootstrapDialog onClose={onClose} open={open} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <SettingsIcon fontSize="small" />
          <Typography variant="h6" component="span">
            Edit {widgetKey ? getWidgetTitle(widgetKey) : 'widget'}
          </Typography>
        </Stack>

        {documentLink ? (
          <Tooltip title={documentLabel}>
            <IconButton
              aria-label={documentLabel}
              to={documentLink}
              component={NavLink}
              target="_blank"
              sx={{ position: 'absolute', right: 48, top: 8 }}
            >
              <HelpIcon />
            </IconButton>
          </Tooltip>
        ) : null}

        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {selectedDetails ? (
          <WidgetsSettings
            handleDetailsChange={onChange}
            SelecedDetails={selectedDetails}
            SelectedDetails={selectedDetails}
            DeviceData={deviceData}
          />
        ) : (
          <Alert severity="info">No widget selected.</Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={onSave} disabled={saving || !selectedDetails}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
}

function DeviceWidgetCard({ item, devicesDetails, deviceData, editable, onMenuOpen }) {
  const widget = Widgets[item.widget];

  if (!widget?.View) {
    return (
        <Grid item  size={{xs:(item.xs?item.xs:12), sm:(item.sm?item.sm:12), md:(item.md?item.md:12), lg:(item.lg?item.lg:12)}}>
      
        <Alert severity="warning">Unknown widget: {item.widget}</Alert>
      </Grid>
    );
  }

  const WidgetView = widget.View;
  const title = item.name || widget.Settings?.Name || item.widget;

  return (
      <Grid item  size={{xs:(item.xs?item.xs:12), sm:(item.sm?item.sm:12), md:(item.md?item.md:12), lg:(item.lg?item.lg:12)}}>
      
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardHeader
          title={title}
          action={
            editable ? (
              <IconButton aria-label="Widget settings" onClick={(event) => onMenuOpen(event, item.id)}>
                <MoreVertIcon />
              </IconButton>
            ) : null
          }
        />
        <CardContent>
          <WidgetErrorBoundary key={`${item.id}-${item.widget}`}>
            <WidgetView DevicesDetails={devicesDetails} DeviceData={deviceData} Settings={item} />
          </WidgetErrorBoundary>
        </CardContent>
      </Card>
    </Grid>
  );
}

export default function View() {
  const { id: deviceId } = useParams();
  const outletContext = useOutletContext() || {};
  const { userRole } = outletContext;
  const editable = canEdit(userRole);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [dataLimit, setDataLimit] = useState(DEFAULT_DATA_LIMIT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [devicesDetails, setDevicesDetails] = useState(null);
  const [deviceDisplay, setDeviceDisplay] = useState([]);
  const [deviceData, setDeviceData] = useState([]);

  const deviceRef = useMemo(() => {
    if (!deviceId) return null;
    return doc(db, 'Devices', deviceId);
  }, [deviceId]);

  const displayCollectionRef = useMemo(() => {
    if (!deviceRef) return null;
    return collection(deviceRef, 'Display');
  }, [deviceRef]);

  const dataCollectionRef = useMemo(() => {
    if (!deviceRef) return null;
    return collection(deviceRef, 'Data');
  }, [deviceRef]);

  const nextWidgetOrder = useMemo(() => {
    return deviceDisplay.reduce((max, item) => Math.max(max, Number(item.widgetOrder) || 0), 0) + 1;
  }, [deviceDisplay]);

  useEffect(() => {
    if (!deviceRef) return undefined;

    return onSnapshot(
      deviceRef,
      (docSnap) => {
        setDevicesDetails(docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null);
      },
      (snapshotError) => {
        console.error(snapshotError);
        setError(snapshotError.message || String(snapshotError));
      }
    );
  }, [deviceRef]);

  useEffect(() => {
    if (!displayCollectionRef) return undefined;

    const displayQuery = query(displayCollectionRef, orderBy('widgetOrder', 'asc'));

    return onSnapshot(
      displayQuery,
      (snapshot) => {
        setDeviceDisplay(snapshot.docs.map((row) => ({ id: row.id, ...row.data() })));
      },
      (snapshotError) => {
        console.error(snapshotError);
        setError(snapshotError.message || String(snapshotError));
      }
    );
  }, [displayCollectionRef]);

  useEffect(() => {
    if (!dataCollectionRef) return undefined;

    const safeLimit = Number.isFinite(Number(dataLimit)) && Number(dataLimit) > 0
      ? Number(dataLimit)
      : DEFAULT_DATA_LIMIT;
    const dataQuery = query(dataCollectionRef, orderBy('dateCreated', 'desc'), limit(safeLimit));

    return onSnapshot(
      dataQuery,
      (snapshot) => {
        setDeviceData(
          snapshot.docs.map((row) => {
            const data = row.data();
            return {
              id: row.id,
              ...data,
              body: parseBody(data.body),
            };
          })
        );
      },
      (snapshotError) => {
        console.error(snapshotError);
        setError(snapshotError.message || String(snapshotError));
      }
    );
  }, [dataCollectionRef, dataLimit]);

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const openMenu = (event, widgetDisplayId) => {
    setSelectedId(widgetDisplayId);
    setMenuAnchorEl(event.currentTarget);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
  };

  const handleDetailsChange = (event, name = null, value = null) => {
    setSelectedDetails((prev) => {
      const current = prev || {};

      if (name) {
        return {
          ...current,
          [name]: value,
        };
      }

      if (!event?.target?.name) return current;

      return {
        ...current,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
      };
    });
  };

  const handleAddWidget = async (widgetKey) => {
    if (!displayCollectionRef) return;

    setError('');
    setSaving(true);

    const newWidgetDetails = makeNewWidgetDetails(widgetKey, nextWidgetOrder);

    try {
      const docRef = await addDoc(displayCollectionRef, {
        ...newWidgetDetails,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSelectedId(docRef.id);
      setSelectedDetails({ id: docRef.id, ...newWidgetDetails });
      setAddDialogOpen(false);
      setEditDialogOpen(true);
    } catch (writeError) {
      console.error(writeError);
      setError(writeError.message || String(writeError));
    } finally {
      setSaving(false);
    }
  };

  const handleEditWidget = () => {
    const selected = deviceDisplay.find((item) => item.id === selectedId);
    closeMenu();

    if (!selected) {
      setError('The selected widget could not be found.');
      return;
    }

    setSelectedDetails({ ...selected });
    setEditDialogOpen(true);
  };

  const handleDeleteWidget = async () => {
    if (!displayCollectionRef || !selectedId) return;

    setError('');
    setSaving(true);

    try {
      await deleteDoc(doc(displayCollectionRef, selectedId));
      setSelectedId(null);
      setSelectedDetails(null);
      closeMenu();
    } catch (writeError) {
      console.error(writeError);
      setError(writeError.message || String(writeError));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWidget = async () => {
    if (!displayCollectionRef || !selectedId || !selectedDetails) return;

    setError('');
    setSaving(true);

    const { id, createdAt, ...detailsToSave } = selectedDetails;

    try {
      await updateDoc(doc(displayCollectionRef, selectedId), {
        ...detailsToSave,
        updatedAt: serverTimestamp(),
      });
      setEditDialogOpen(false);
    } catch (writeError) {
      console.error(writeError);
      setError(writeError.message || String(writeError));
    } finally {
      setSaving(false);
    }
  };

  const WelcomeView = Widgets.Welcome?.View;

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack spacing={2}>
        {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}

        {false && editable ? (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Box>
              <Typography variant="h6">Device widgets</Typography>
              <Typography variant="body2" color="text.secondary">
                Showing {deviceDisplay.length} widget{deviceDisplay.length === 1 ? '' : 's'} and {deviceData.length} data entr{deviceData.length === 1 ? 'y' : 'ies'}.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                label="Data limit"
                type="number"
                size="small"
                value={dataLimit}
                onChange={(event) => setDataLimit(event.target.value)}
                sx={{ width: 130 }}
                inputProps={{ min: 1 }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
              >
                Add widget
              </Button>
            </Stack>
          </Stack>
        ) : null}

        <Grid container spacing={3}>
          {deviceDisplay.length === 0 && WelcomeView ? (
              <Grid item  size={{xs:(12), sm:(12), md:(12), lg:(12)}}>

              <Card variant="outlined">
                <CardHeader title="Welcome" />
                <CardContent>
                  <WelcomeView />
                </CardContent>
              </Card>
            </Grid>
          ) : null}

          {deviceDisplay.map((item) => (
            <DeviceWidgetCard
              key={item.id}
              item={item}
              devicesDetails={devicesDetails}
              deviceData={deviceData}
              editable={editable}
              onMenuOpen={openMenu}
            />
          ))}
        </Grid>
      </Stack>

      {editable ? (
        <Box sx={{ mt: 3, mb: 7, textAlign: 'center' }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add widget
          </Button>
        </Box>
      ) : null}

      <AddWidgetDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddWidget}
      />

      <EditWidgetDialog
        open={editDialogOpen}
        selectedDetails={selectedDetails}
        deviceData={deviceData}
        saving={saving}
        onClose={closeEditDialog}
        onSave={handleSaveWidget}
        onChange={handleDetailsChange}
      />

      <Menu
        id="widget-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
      >
        <MenuItem onClick={handleEditWidget}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SettingsIcon fontSize="small" />
            <span>Edit</span>
          </Stack>
        </MenuItem>
        <MenuItem onClick={handleDeleteWidget} disabled={saving}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DeleteIcon fontSize="small" />
            <span>Delete</span>
          </Stack>
        </MenuItem>
      </Menu>
    </Box>
  );
}
